import api from "../axiosConfig";

export const USERS_SETTINGS_CACHE_UPDATED_EVENT =
  "loadevo:users-settings-cache-updated";

export const USERS_SETTINGS_ROLE_KEYS = [
  "admin",
  "manager",
  "dispatcher",
  "accountant",
];

const USERS_SETTINGS_CACHE_VERSION =
  "v3";

const USERS_SETTINGS_CACHE_MAX_AGE =
  180 *
  24 *
  60 *
  60 *
  1000;

const USERS_SETTINGS_FRESH_FOR =
  60 *
  1000;

const LEGACY_CACHE_PREFIX =
  "usersSettings.users.v2";

const ACTIVE_ROLE_KEY =
  "users.activeRoleTab";

/*
 * --------------------------------------------------------------------------
 * Runtime state
 * --------------------------------------------------------------------------
 */

const hotCache =
  new Map();

const inFlightRequests =
  new Map();

const requestSequences =
  new Map();

const latestRequestIds =
  new Map();

const localPatchRevisions =
  new Map();

const warmedAvatars =
  new Map();


/*
 * --------------------------------------------------------------------------
 * Helpers
 * --------------------------------------------------------------------------
 */

const safeParse =
  (
    value
  ) => {
    try {
      return JSON.parse(
        value
      );
    } catch {
      return null;
    }
  };


const decodeJwtPayload =
  (
    token
  ) => {
    if (!token) {
      return null;
    }

    try {
      const part =
        token
          .split(".")[1]
          ?.replace(
            /-/g,
            "+"
          )
          .replace(
            /_/g,
            "/"
          );

      if (!part) {
        return null;
      }

      const padded =
        part +
        "=".repeat(
          (
            4 -
            (
              part.length %
              4
            )
          ) %
            4
        );

      return JSON.parse(
        window.atob(
          padded
        )
      );
    } catch {
      return null;
    }
  };


const hashString =
  (
    value
  ) => {
    let hash =
      2166136261;

    const source =
      String(
        value ||
          ""
      );

    for (
      let index = 0;
      index <
      source.length;
      index += 1
    ) {
      hash ^=
        source.charCodeAt(
          index
        );

      hash =
        Math.imul(
          hash,
          16777619
        );
    }

    return (
      hash >>>
      0
    ).toString(
      36
    );
  };


const getStoredToken =
  () => {
    if (
      typeof window ===
      "undefined"
    ) {
      return "";
    }

    return (
      window.localStorage.getItem(
        "authToken"
      ) ||
      window.sessionStorage.getItem(
        "authToken"
      ) ||
      window.localStorage.getItem(
        "token"
      ) ||
      window.localStorage.getItem(
        "accessToken"
      ) ||
      ""
    );
  };


const resolveScope =
  () => {
    if (
      typeof window ===
      "undefined"
    ) {
      return "server";
    }

    const token =
      getStoredToken();

    const payload =
      decodeJwtPayload(
        token
      );

    const companyId =
      payload?.company_id ??
      payload?.companyId ??
      null;

    if (
      companyId !==
        null &&
      companyId !==
        undefined &&
      String(
        companyId
      ).trim() !==
        ""
    ) {
      return `company-${companyId}`;
    }

    if (token) {
      return `token-${hashString(
        token
      )}`;
    }

    return "anonymous";
  };


const normalizeRole =
  (
    role
  ) =>
    USERS_SETTINGS_ROLE_KEYS.includes(
      role
    )
      ? role
      : "admin";


const buildCacheKey =
  (
    role
  ) =>
    `loadevo.users-settings.${resolveScope()}.${normalizeRole(
      role
    )}.${USERS_SETTINGS_CACHE_VERSION}`;


const absoluteFromApiBase =
  (
    path
  ) => {
    if (!path) {
      return null;
    }

    if (
      /^https?:\/\//i.test(
        path
      )
    ) {
      return path;
    }

    const base = (
      api.defaults?.baseURL ||
      ""
    ).replace(
      /\/$/,
      ""
    );

    return base
      ? `${base}${
          path.startsWith("/")
            ? ""
            : "/"
        }${path}`
      : path;
  };


export const normalizeMiniUser =
  (
    user = {}
  ) => ({
    id:
      user.id ??
      user.user_id ??
      null,

    user_id:
      user.user_id ??
      user.id ??
      null,

    name:
      user.name ??
      "",

    role:
      String(
        user.role ||
          ""
      ).toLowerCase(),

    phoneNumber:
      user.phoneNumber ??
      user.phone_number ??
      "",

    email:
      user.email ??
      "",

    status:
      user.status ??
      "active",

    profilePictureUrl:
      absoluteFromApiBase(
        user.profilePictureUrl ??
        user.profile_picture_url ??
        user.profilePicture ??
        null
      ),
  });


const sameId =
  (
    first,
    second
  ) =>
    first !==
      null &&
    first !==
      undefined &&
    second !==
      null &&
    second !==
      undefined &&
    String(first) ===
      String(second);


/*
 * --------------------------------------------------------------------------
 * Avatar warming
 * --------------------------------------------------------------------------
 */

const prewarmAvatar =
  (
    url
  ) => {
    if (
      typeof window ===
        "undefined" ||
      !url
    ) {
      return Promise.resolve(
        null
      );
    }

    if (
      warmedAvatars.has(
        url
      )
    ) {
      return warmedAvatars.get(
        url
      ).promise;
    }

    const image =
        new Image();
    
    image.decoding =
        "sync";

    const promise =
      new Promise(
        (
          resolve
        ) => {
          let finished =
            false;

          const finish =
            async () => {
              if (finished) {
                return;
              }

              finished =
                true;

              try {
                if (
                  typeof image.decode ===
                  "function"
                ) {
                  await image.decode();
                }
              } catch {
                // onload is still enough
              }

              resolve(
                url
              );
            };

          image.onload =
            finish;

          image.onerror =
            () => {
              warmedAvatars.delete(
                url
              );

              resolve(
                null
              );
            };

          image.src =
            url;

          if (
            image.complete &&
            image.naturalWidth >
              0
          ) {
            finish();
          }
        }
      );

    warmedAvatars.set(
      url,
      {
        image,
        promise,
      }
    );

    return promise;
  };


export const prewarmUsersSettingsRoleAvatars =
  (
    role,
    {
      limit = 18,
    } = {}
  ) => {
    const cached =
      readUsersSettingsRoleCache(
        role
      );

    if (!cached) {
      return Promise.resolve(
        []
      );
    }

    const urls =
      cached.users
        .map(
          (
            user
          ) =>
            user.profilePictureUrl
        )
        .filter(Boolean)
        .slice(
          0,
          limit
        );

    return Promise.allSettled(
      urls.map(
        prewarmAvatar
      )
    );
  };


export const prewarmUsersSettingsAvatarsFromCache =
  () => {
    if (
      typeof window ===
      "undefined"
    ) {
      return Promise.resolve(
        []
      );
    }

    const activeRole =
      normalizeRole(
        window.localStorage.getItem(
          ACTIVE_ROLE_KEY
        ) ||
        "admin"
      );

    return prewarmUsersSettingsRoleAvatars(
      activeRole
    );
  };


/*
 * --------------------------------------------------------------------------
 * Legacy migration
 * --------------------------------------------------------------------------
 */

const readLegacyRoleCache =
  (
    role
  ) => {
    try {
      const raw =
        window.sessionStorage.getItem(
          `${LEGACY_CACHE_PREFIX}.${role}`
        );

      if (!raw) {
        return null;
      }

      const parsed =
        safeParse(
          raw
        );

      if (
        !Array.isArray(
          parsed?.users
        )
      ) {
        return null;
      }

      return {
        users:
          parsed.users.map(
            normalizeMiniUser
          ),

        fetchedAt:
          typeof parsed.savedAt ===
            "number"
            ? parsed.savedAt
            : 0,
      };
    } catch {
      return null;
    }
  };


/*
 * --------------------------------------------------------------------------
 * Read/write
 * --------------------------------------------------------------------------
 */

export const readUsersSettingsRoleCache =
  (
    role
  ) => {
    if (
      typeof window ===
      "undefined"
    ) {
      return null;
    }

    const normalizedRole =
      normalizeRole(
        role
      );

    const cacheKey =
      buildCacheKey(
        normalizedRole
      );

    const hot =
      hotCache.get(
        cacheKey
      );

    if (
      hot?.data &&
      Date.now() -
        hot.savedAt <=
        USERS_SETTINGS_CACHE_MAX_AGE
    ) {
      return hot.data;
    }

    try {
      const raw =
        window.localStorage.getItem(
          cacheKey
        );

      if (!raw) {
        const legacy =
          readLegacyRoleCache(
            normalizedRole
          );

        if (legacy) {
          writeUsersSettingsRoleCache(
            normalizedRole,
            legacy.users,
            {
              fetchedAt:
                legacy.fetchedAt,
            }
          );

          return legacy;
        }

        return null;
      }

      const parsed =
        safeParse(
          raw
        );

      if (
        !parsed ||
        !parsed.data ||
        !Array.isArray(
          parsed.data.users
        ) ||
        typeof parsed.savedAt !==
          "number"
      ) {
        window.localStorage.removeItem(
          cacheKey
        );

        return null;
      }

      if (
        Date.now() -
          parsed.savedAt >
        USERS_SETTINGS_CACHE_MAX_AGE
      ) {
        window.localStorage.removeItem(
          cacheKey
        );

        hotCache.delete(
          cacheKey
        );

        return null;
      }

      hotCache.set(
        cacheKey,
        parsed
      );

      return parsed.data;
    } catch (
      error
    ) {
      console.warn(
        "[usersSettingsCache] read failed:",
        error
      );

      return null;
    }
  };


export const writeUsersSettingsRoleCache =
  (
    role,
    users,
    {
      fetchedAt =
        Date.now(),
    } = {}
  ) => {
    const normalizedRole =
      normalizeRole(
        role
      );

    const nextData = {
      users:
        Array.isArray(
          users
        )
          ? users.map(
              normalizeMiniUser
            )
          : [],

      fetchedAt,
    };

    if (
      typeof window ===
      "undefined"
    ) {
      return nextData;
    }

    const cacheKey =
      buildCacheKey(
        normalizedRole
      );

    const envelope = {
      savedAt:
        Date.now(),

      data:
        nextData,
    };

    hotCache.set(
      cacheKey,
      envelope
    );

    try {
      window.localStorage.setItem(
        cacheKey,
        JSON.stringify(
          envelope
        )
      );
    } catch (
      error
    ) {
      console.warn(
        "[usersSettingsCache] write failed:",
        error
      );
    }

    window.dispatchEvent(
      new CustomEvent(
        USERS_SETTINGS_CACHE_UPDATED_EVENT,
        {
          detail: {
            role:
              normalizedRole,

            data:
              nextData,
          },
        }
      )
    );

    return nextData;
  };


export const patchUsersSettingsRoleCache =
  (
    role,
    updater
  ) => {
    const normalizedRole =
      normalizeRole(
        role
      );

    const currentRevision =
      localPatchRevisions.get(
        normalizedRole
      ) ||
      0;

    localPatchRevisions.set(
      normalizedRole,
      currentRevision +
        1
    );

    const current =
      readUsersSettingsRoleCache(
        normalizedRole
      ) || {
        users: [],
        fetchedAt: 0,
      };

    const nextUsers =
      typeof updater ===
      "function"
        ? updater(
            current.users
          )
        : updater;

    return writeUsersSettingsRoleCache(
      normalizedRole,
      nextUsers,
      {
        /*
         * A local/WS patch does not pretend that a full HTTP
         * revalidation just happened.
         */
        fetchedAt:
          current.fetchedAt ||
          0,
      }
    );
  };


export const readAllUsersSettingsRoleCaches =
  () => {
    const result = {};

    USERS_SETTINGS_ROLE_KEYS.forEach(
      (
        role
      ) => {
        const cached =
          readUsersSettingsRoleCache(
            role
          );

        if (cached) {
          result[
            role
          ] =
            cached;
        }
      }
    );

    return result;
  };


/*
 * --------------------------------------------------------------------------
 * Subscription
 * --------------------------------------------------------------------------
 */

export const subscribeUsersSettingsRoleCache =
  (
    role,
    callback
  ) => {
    if (
      typeof window ===
        "undefined" ||
      typeof callback !==
        "function"
    ) {
      return () => {};
    }

    const normalizedRole =
      normalizeRole(
        role
      );

    const cacheKey =
      buildCacheKey(
        normalizedRole
      );

    const customHandler =
      (
        event
      ) => {
        if (
          event?.detail?.role !==
          normalizedRole
        ) {
          return;
        }

        callback(
          event.detail.data
        );
      };

    const storageHandler =
      (
        event
      ) => {
        if (
          event.key !==
          cacheKey
        ) {
          return;
        }

        hotCache.delete(
          cacheKey
        );

        callback(
          readUsersSettingsRoleCache(
            normalizedRole
          )
        );
      };

    window.addEventListener(
      USERS_SETTINGS_CACHE_UPDATED_EVENT,
      customHandler
    );

    window.addEventListener(
      "storage",
      storageHandler
    );

    return () => {
      window.removeEventListener(
        USERS_SETTINGS_CACHE_UPDATED_EVENT,
        customHandler
      );

      window.removeEventListener(
        "storage",
        storageHandler
      );
    };
  };


/*
 * --------------------------------------------------------------------------
 * Network
 * --------------------------------------------------------------------------
 */

const buildRequestConfig =
  (
    role,
    force
  ) => {
    const config = {
      params: {
        role,
        page: 1,
        limit: 100,
      },
    };

    if (force) {
      config.params._usersSettingsRefresh =
        Date.now();

      config.headers = {
        "Cache-Control":
          "no-cache",

        Pragma:
          "no-cache",
      };
    }

    return config;
  };


export const prefetchUsersSettingsRole =
  async (
    role,
    {
      force = false,
    } = {}
  ) => {
    const normalizedRole =
      normalizeRole(
        role
      );

    const cached =
      readUsersSettingsRoleCache(
        normalizedRole
      );

    if (
      !force &&
      cached?.fetchedAt &&
      Date.now() -
        cached.fetchedAt <
        USERS_SETTINGS_FRESH_FOR
    ) {
      return cached;
    }

    if (
      !force &&
      inFlightRequests.has(
        normalizedRole
      )
    ) {
      return inFlightRequests.get(
        normalizedRole
      );
    }

    const requestId =
      (
        requestSequences.get(
          normalizedRole
        ) ||
        0
      ) +
      1;

    requestSequences.set(
      normalizedRole,
      requestId
    );

    latestRequestIds.set(
      normalizedRole,
      requestId
    );

    const patchRevisionAtStart =
      localPatchRevisions.get(
        normalizedRole
      ) ||
      0;

    const requestPromise =
      (async () => {
        try {
          const {
            data,
          } =
            await api.get(
              "/users/mini",
              buildRequestConfig(
                normalizedRole,
                force
              )
            );

          if (
            latestRequestIds.get(
              normalizedRole
            ) !==
              requestId ||
            (
              localPatchRevisions.get(
                normalizedRole
              ) ||
              0
            ) !==
              patchRevisionAtStart
          ) {
            return (
              readUsersSettingsRoleCache(
                normalizedRole
              ) ||
              cached ||
              null
            );
          }

          const users =
            Array.isArray(
              data?.data
            )
              ? data.data.map(
                  normalizeMiniUser
                )
              : [];

          return writeUsersSettingsRoleCache(
            normalizedRole,
            users,
            {
              fetchedAt:
                Date.now(),
            }
          );
        } catch (
          error
        ) {
          if (cached) {
            return cached;
          }

          throw error;
        }
      })();

    inFlightRequests.set(
      normalizedRole,
      requestPromise
    );

    try {
      return await requestPromise;
    } finally {
      if (
        inFlightRequests.get(
          normalizedRole
        ) ===
        requestPromise
      ) {
        inFlightRequests.delete(
          normalizedRole
        );
      }
    }
  };


/*
 * --------------------------------------------------------------------------
 * Shared upsert/remove helpers
 * --------------------------------------------------------------------------
 */

export const upsertUserIntoRoleCache =
  (
    role,
    user
  ) => {
    const normalized =
      normalizeMiniUser(
        user
      );

    if (!normalized.id) {
      return null;
    }

    return patchUsersSettingsRoleCache(
      role,
      (
        previousUsers
      ) => {
        const index =
          previousUsers.findIndex(
            (
              current
            ) =>
              sameId(
                current.id ??
                  current.user_id,
                normalized.id
              )
          );

        if (index === -1) {
          return [
            ...previousUsers,
            normalized,
          ].sort(
            (
              first,
              second
            ) =>
              String(
                first.name ||
                  ""
              ).localeCompare(
                String(
                  second.name ||
                    ""
                )
              )
          );
        }

        const copy = [
          ...previousUsers,
        ];

        copy[
          index
        ] = normalizeMiniUser({
          ...copy[
            index
          ],
          ...normalized,
        });

        return copy;
      }
    );
  };


export const removeUserFromRoleCache =
  (
    role,
    userId
  ) => {
    const cached =
      readUsersSettingsRoleCache(
        role
      );

    /*
     * Do not create a fake empty cache for a role
     * which has never been loaded.
     */
    if (!cached) {
      return null;
    }

    return patchUsersSettingsRoleCache(
      role,
      (
        previousUsers
      ) =>
        previousUsers.filter(
          (
            user
          ) =>
            !sameId(
              user.id ??
                user.user_id,
              userId
            )
        )
    );
  };